// CSV bulk-import multi-step modal orchestration.
//
// Sub-commit 4: Steps 1-2 only — file pick → parse → preview first 5 rows.
// Mapping, validation, and submit come in later sub-commits. Each step is its
// own openModal call (close + reopen between steps), matching the canonical
// Huddledin module pattern documented in fix-csv-01 §D1.
//
// Cancel semantics mirror reports/index.js:762-779: each button's onClick
// calls resolve(...) explicitly. If the user dismisses via X or backdrop,
// the step's promise stays pending and the async caller hangs at its await.
// That IS the cancel — no downstream code runs, the orphan closure GCs on
// next nav, and re-triggering the menu item opens a fresh flow.

import { parseCsvFile, MAX_FILE_BYTES } from './parse.js';
import { HUDDLEDIN_FIELDS, FIELD_LABELS, NAME_SOURCE_FIELDS, suggestMapping } from './mapping.js';
import { buildPreview } from './validate.js';

function H() { return window.HUD || {}; }
const T = (k, p) => window.HUD?.T?.(k, p) || k;

// Sample-CSV template content — generated client-side on link click, no
// static file shipped. Headers match the mapping heuristics exactly so a
// downloaded-then-filled template auto-maps 100% on re-upload. ISO date
// format. CRLF line endings (Excel / Numbers prefer it; PapaParse handles
// either).
const CSV_TEMPLATE_CONTENT = [
  'first_name,last_name,dob,pronouns,parent_name_hint,parent_email_hint,notes',
  'Example,DeleteThisRow,2018-04-25,she,Parent Name,parent@example.com,Delete the example rows before importing',
  'Sample,DeleteThisRow,2019-11-30,he,,,These rows just show the format',
].join('\r\n');

// Silent-download method: Blob → createObjectURL → temporary anchor with the
// `download` attribute set → programmatic click() → revoke. The `download`
// attribute is what tells the browser to save-as-file rather than navigate;
// it does NOT bypass the user's "Ask where to save each file" browser
// setting (that's user-controlled, page can't override).
// Strip null / empty-string keys from a resolved patient before sending to
// the RPC. The function's NULLIF(_row->>'X','') treats absent, null, and ''
// identically — sending only populated keys is the cleanest of the three
// and keeps the wire payload small for large imports.
function _toRpcRow(patient) {
  const out = {};
  for (const k of ['name', 'dob', 'pronouns', 'parent_name_hint', 'parent_email_hint', 'notes']) {
    const v = patient[k];
    if (v != null && v !== '') out[k] = v;
  }
  return out;
}

// Shared client-side CSV download. Used by both the template download
// (SC5.1) and the error-report download (SC8). Silent-save via the
// download attribute on a temporary anchor; browser settings still
// control where the file lands.
function _downloadCsv(content, filename) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  // Anchor must be in the DOM for Firefox to honor the download attr.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke by one tick — Safari sometimes hasn't started streaming
  // by the time the synchronous click() returns.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// Escape one cell for CSV. Quote if the value contains a delimiter,
// embedded quote, or any line break; embedded quotes are doubled per the
// CSV convention.
function _csvEscape(s) {
  const v = s == null ? '' : String(s);
  return /[",\r\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v;
}

// Build the error-report CSV for a partial-success import. One row per
// FAILED row from the RPC. Columns = the ORIGINAL CSV headers the user
// uploaded (so they can fix the file in place) plus an appended `error`
// column with the per-row reason.
//
// Row-index mapping (the one tricky bit — SC8 spec called this out):
//   - errors[].row is the index into the PAYLOAD array that was sent to
//     the RPC, which was filtered (invalids + skipped dupes dropped).
//   - includedStates is the parallel array of rowStates we filtered into
//     the payload (same length, same order).
//   - rowState.rowIndex points back to the original parsedRows index.
//   So: errors[k].row → includedStates[errors[k].row] → that rowState's
//   .rowIndex → parsedRows[...] is the raw CSV row to surface.
function _buildErrorReportCsv(errors, includedStates, parsedRows, headers) {
  const lines = [];
  // Header line: original headers verbatim + 'error' column appended.
  lines.push([...headers, 'error'].map(_csvEscape).join(','));
  for (const err of (errors || [])) {
    const rowState = includedStates[err.row];
    if (!rowState) continue; // defensive — should never happen
    const origRow = parsedRows[rowState.rowIndex] || {};
    const cells = headers.map(h => _csvEscape(origRow[h] != null ? origRow[h] : ''));
    cells.push(_csvEscape(err.reason || ''));
    lines.push(cells.join(','));
  }
  return lines.join('\r\n');
}

function _downloadTemplate() {
  _downloadCsv(CSV_TEMPLATE_CONTENT, 'huddledin-patient-template.csv');
}

// ── Public entry — wired into window.HUD_CSV_IMPORT.open() from index.js ──
export async function openImportFlow() {
  const { session, toast } = H();

  // Demo gate — mirror _showCreateSpecPatientModal:18732 in index.html.
  if (session?.id === 'demo') {
    toast?.(T('csv_demo_blocked'), 'info');
    return;
  }

  const parseResult = await _stepPickFile();
  if (!parseResult) return;

  // Linear step machine — each step says where to go next via the `step`
  // variable. Back arrows pop one step backward. Cancel / X / backdrop
  // at any step exits the flow (matches the canonical pending-promise-as-
  // cancel semantic at top of this file). `mapping` is held in the outer
  // scope so the validation step can consume it AND so a future SC could
  // pass it back into _stepMapping to preserve user edits on Back trips
  // (current behavior: mapping re-runs suggestMapping each open, edits
  // are lost — same limitation noted in SC5's build-log).
  let step = 'preview';
  let mapping = null;
  while (step) {
    if (step === 'preview') {
      const r = await _stepPreview(parseResult);
      if (r !== 'next') return;
      step = 'mapping';
    } else if (step === 'mapping') {
      const r = await _stepMapping(parseResult);
      if (r === 'back') { step = 'preview'; continue; }
      if (!r || r.action !== 'next') return;
      mapping = r.mapping;
      step = 'validation';
    } else if (step === 'validation') {
      const r = await _stepValidationPreview(parseResult, mapping);
      if (r === 'back') { step = 'mapping'; continue; }
      if (!r || r.action !== 'imported') return;
      // RPC succeeded inside the validation step (which also fired the
      // user-facing toast + ran the suppress-wrapped refetch). The result
      // payload (inserted/total/errors/rowStates) is on r for SC8's error-
      // report download — for now we just exit cleanly.
      return;
    }
  }
}

// ── Step 1 — file picker ─────────────────────────────────────────────────
function _stepPickFile() {
  return new Promise(resolve => {
    const { openModal, el, mkBtn } = H();
    if (!openModal) { resolve(null); return; }

    openModal(T('csv_pick_title'), (mb, close) => {
      mb.classList.add('csv-modal-body');

      mb.appendChild(el('p', {
        style: { color: 'var(--slate)', fontSize: '.84rem', lineHeight: '1.5', marginBottom: '14px' },
      }, [T('csv_pick_desc')]));

      const fileInput = el('input', {
        type: 'file',
        accept: '.csv',
        class: 'inp csv-file-input',
      });

      const hint = el('div', { class: 'csv-hint' }, [T('csv_local_hint')]);

      // Bad-header sentinel: NUL + low control chars (excluding tab/LF/CR/FF)
      // + DEL + U+FFFD. PapaParse reads files as UTF-8 by default; invalid
      // byte sequences (PNG/JPEG/binary) decode to U+FFFD, and ASCII control
      // bytes also signal non-text content. If any parsed header contains
      // one of these we treat the file as not-a-CSV regardless of extension.
      const BAD_HEADER_CHAR = /[\x00-\x08\x0E-\x1F\x7F\uFFFD]/;

      const _resetPickStep = () => {
        fileInput.disabled = false;
        fileInput.value = '';
        hint.textContent = T('csv_local_hint');
      };

      // Shared file handler — both fileInput.onchange and the drop handler
      // route here so the extension / size / parse / empty-rows / content-
      // sanity guards run identically for picked AND dropped files.
      const handleFile = async (file) => {
        const { toast } = H();
        if (!file) return;

        // Extension guard — accept=".csv" only filters the picker dialog;
        // users can still pick "All files" and choose a PNG, or drop one
        // onto the zone. Reject anything not ending in .csv before parse.
        if (!/\.csv$/i.test(file.name)) {
          toast?.(T('csv_choose_csv'), 'error');
          fileInput.value = '';
          return;
        }

        // Explicit size guard — match the spec's user-facing wording. parse.js
        // also rejects oversized files, but its message uses "KB" units; we
        // surface the friendlier "1 MB" here and short-circuit before parsing.
        if (file.size > MAX_FILE_BYTES) {
          toast?.(T('csv_too_large'), 'error');
          fileInput.value = '';
          return;
        }

        fileInput.disabled = true;
        hint.textContent = T('csv_parsing');
        try {
          const result = await parseCsvFile(file);
          if (!result.totalRows) {
            toast?.(T('csv_no_rows'), 'error');
            _resetPickStep();
            return;
          }
          // Content sanity guard — catches files that passed the extension
          // check but aren't actually text-CSV (binary renamed to .csv,
          // non-UTF-8 encoding, etc.). Any bad char in any header is enough.
          const hasBadHeader = (result.headers || []).some(h => BAD_HEADER_CHAR.test(h || ''));
          if (hasBadHeader) {
            toast?.(T('csv_unreadable'), 'error');
            _resetPickStep();
            return;
          }
          close();
          resolve({ ...result, _fileName: file.name });
        } catch (err) {
          const msg = err?.message || T('csv_parse_failed');
          toast?.(msg, 'error');
          _resetPickStep();
        }
      };

      fileInput.onchange = () => {
        const f = fileInput.files?.[0];
        if (f) handleFile(f);
      };

      // Drop zone — wraps the input and the "drag a .csv here" prompt. The
      // zone is also clickable (anywhere except the native input itself) to
      // open the picker, so users get three paths to the same handleFile().
      const dropZone = el('div', { class: 'csv-dropzone' });
      const dropPrompt = el('div', { class: 'csv-dropzone-hint' }, [T('csv_drop_prompt')]);
      dropZone.appendChild(dropPrompt);
      dropZone.appendChild(fileInput);

      dropZone.addEventListener('click', (e) => {
        // Don't recurse when the native input was clicked directly — that
        // already opens the picker.
        if (e.target === fileInput) return;
        fileInput.click();
      });

      dropZone.addEventListener('dragover', (e) => {
        // preventDefault is REQUIRED on both dragover and drop, otherwise
        // the browser navigates to the file instead of firing our handler.
        e.preventDefault();
        dropZone.classList.add('csv-dropzone-active');
      });

      dropZone.addEventListener('dragleave', (e) => {
        // dragleave fires when crossing into child elements too. Only clear
        // the active state when the cursor truly left the drop zone (no
        // related target, or related target is outside the zone).
        if (!dropZone.contains(e.relatedTarget)) {
          dropZone.classList.remove('csv-dropzone-active');
        }
      });

      dropZone.addEventListener('drop', (e) => {
        e.preventDefault();
        dropZone.classList.remove('csv-dropzone-active');
        const f = e.dataTransfer?.files?.[0];
        if (f) handleFile(f);
      });

      mb.appendChild(dropZone);
      mb.appendChild(hint);

      // Template download — outlined secondary button. Same btn-md
      // btn-secondary convention as reports' "Upload template" button
      // (src/features/reports/index.js:409). marginInlineEnd:'auto' is the
      // flex "push apart" trick: csv-btn-row's justify-content:flex-end
      // normally hugs everything right, but an auto inline-end margin on
      // this first child consumes the remaining space, pinning template
      // to the row's start (RTL-safe via the logical property) and Cancel
      // to the row's end.
      const btnRow = el('div', { class: 'csv-btn-row' });
      const tplBtn = mkBtn('⬇ ' + T('csv_download_template'), 'btn-md btn-secondary', _downloadTemplate);
      tplBtn.style.marginInlineEnd = 'auto';
      btnRow.appendChild(tplBtn);
      btnRow.appendChild(mkBtn(T('btn_cancel'), 'btn-md btn-ghost', () => { close(); resolve(null); }));
      mb.appendChild(btnRow);
    }, 520);
  });
}

// ── Step 2 — preview ─────────────────────────────────────────────────────
function _stepPreview(parseResult) {
  return new Promise(resolve => {
    const { openModal, el, mkBtn } = H();
    if (!openModal) { resolve(null); return; }

    openModal(T('csv_preview_title'), (mb, close) => {
      mb.classList.add('csv-modal-body');

      const _n = parseResult.totalRows;
      mb.appendChild(el('p', {
        style: { color: 'var(--navy)', fontWeight: 600, fontSize: '.86rem', marginBottom: '4px' },
      }, [T(_n === 1 ? 'csv_rows_detected' : 'csv_rows_detected_plural', { count: _n, file: parseResult._fileName || 'file' })]));

      mb.appendChild(el('p', {
        style: { color: 'var(--slate)', fontSize: '.74rem', marginBottom: '14px' },
      }, [T('csv_preview_note')]));

      const tableWrap = el('div', { class: 'csv-preview-wrap' });
      const table = el('table', { class: 'csv-preview-table' });

      const thead = el('thead');
      const headRow = el('tr');
      const headers = parseResult.headers || [];
      headers.forEach(h => {
        headRow.appendChild(el('th', {}, [(h && h.length) ? h : T('csv_blank')]));
      });
      thead.appendChild(headRow);
      table.appendChild(thead);

      const tbody = el('tbody');
      const previewRows = (parseResult.rows || []).slice(0, 5);
      previewRows.forEach(row => {
        const tr = el('tr');
        headers.forEach(h => {
          const v = (row && row[h] != null) ? String(row[h]) : '';
          tr.appendChild(el('td', {}, [v]));
        });
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);
      tableWrap.appendChild(table);
      mb.appendChild(tableWrap);

      const btnRow = el('div', { class: 'csv-btn-row' });
      btnRow.appendChild(mkBtn(T('btn_cancel'), 'btn-md btn-ghost', () => { close(); resolve(null); }));
      btnRow.appendChild(mkBtn(T('btn_next'), 'btn-md btn-primary', () => { close(); resolve('next'); }));
      mb.appendChild(btnRow);
    }, 720);
  });
}

// ── Step 3 — column mapping ──────────────────────────────────────────────
// Resolves to one of:
//   { action: 'next', mapping: { csvHeader: huddledinField | null } } — user
//     mapped at least the required "name" field and clicked Next.
//   'back' — user clicked ← Back; caller should re-open the preview step.
//   null — user dismissed via X / backdrop (matches the canonical cancel
//     semantic at the top of this file).
function _stepMapping(parseResult) {
  return new Promise(resolve => {
    const { openModal, el, mkBtn } = H();
    if (!openModal) { resolve(null); return; }

    const headers = parseResult.headers || [];
    const firstRow = (parseResult.rows || [])[0] || {};

    // Working state — mutated by each <select>'s onchange. Each csvHeader
    // points to a huddledinField string, or null for "(don't import)".
    const mapping = suggestMapping(headers);

    openModal(T('csv_map_title'), (mb, close) => {
      mb.classList.add('csv-modal-body');

      mb.appendChild(el('p', {
        style: { color: 'var(--slate)', fontSize: '.84rem', lineHeight: '1.5', marginBottom: '14px' },
      }, [T('csv_map_desc')]));

      const rowsWrap = el('div', { class: 'csv-mapping-wrap' });

      headers.forEach(h => {
        const row = el('div', { class: 'csv-mapping-row' });

        const leftCol = el('div', { class: 'csv-mapping-left' });
        leftCol.appendChild(el('div', { class: 'csv-mapping-header-label' }, [(h && h.length) ? h : T('csv_blank')]));
        const sampleRaw = firstRow[h] != null ? String(firstRow[h]) : '';
        const sampleTrunc = sampleRaw.length > 60 ? sampleRaw.slice(0, 60) + '…' : sampleRaw;
        const sampleText = sampleRaw.length ? T('csv_sample', { val: sampleTrunc }) : T('csv_sample_empty');
        leftCol.appendChild(el('div', { class: 'csv-mapping-sample' }, [sampleText]));
        row.appendChild(leftCol);

        const sel = el('select', { class: 'csv-mapping-select' });
        sel.appendChild(el('option', { value: '' }, [T('csv_dont_import')]));
        HUDDLEDIN_FIELDS.forEach(f => {
          const opt = el('option', { value: f }, [T(FIELD_LABELS[f])]);
          if (mapping[h] === f) opt.selected = true;
          sel.appendChild(opt);
        });
        sel.onchange = () => {
          mapping[h] = sel.value || null;
          _updateGate();
        };
        row.appendChild(sel);

        rowsWrap.appendChild(row);
      });

      mb.appendChild(rowsWrap);

      const status = el('div', { class: 'csv-mapping-status' });
      mb.appendChild(status);

      const btnRow = el('div', { class: 'csv-btn-row' });
      const backBtn = mkBtn(T('btn_back'), 'btn-md btn-ghost', () => { close(); resolve('back'); });
      const nextBtn = mkBtn(T('btn_next'), 'btn-md btn-primary', () => {
        // Defensive — the button is disabled when the gate fails, but a
        // synthetic click could still arrive. Re-check.
        if (_currentGateState() !== 'ok') return;
        close();
        resolve({ action: 'next', mapping: { ...mapping } });
      });

      // Gate states:
      //   'ok'          — at least one of name / first_name / last_name is
      //                   mapped, and none of those three is mapped to more
      //                   than one column. (Other fields can be duplicated
      //                   freely — only the name trio is uniqueness-checked
      //                   here; SC6 will handle the rest of validation.)
      //   'missing'     — none of the three name sources is mapped.
      //   'dup:<field>' — that one of the three is mapped to >1 column.
      function _currentGateState() {
        const counts = { name: 0, first_name: 0, last_name: 0 };
        for (const v of Object.values(mapping)) {
          if (v && (v in counts)) counts[v]++;
        }
        for (const f of NAME_SOURCE_FIELDS) {
          if (counts[f] > 1) return 'dup:' + f;
        }
        const haveSomeName = NAME_SOURCE_FIELDS.some(f => counts[f] >= 1);
        if (!haveSomeName) return 'missing';
        return 'ok';
      }

      function _updateGate() {
        const s = _currentGateState();
        let msg = '';
        if (s === 'missing') {
          msg = T('csv_gate_missing');
        } else if (s.startsWith('dup:')) {
          const field = s.slice(4);
          msg = T('csv_gate_dup', { field: T(FIELD_LABELS[field]) });
        }
        status.textContent = msg;
        if (s === 'ok') {
          status.classList.remove('csv-mapping-status-warn');
          nextBtn.disabled = false;
        } else {
          status.classList.add('csv-mapping-status-warn');
          nextBtn.disabled = true;
        }
      }

      btnRow.appendChild(backBtn);
      btnRow.appendChild(nextBtn);
      mb.appendChild(btnRow);

      _updateGate(); // initial pass — handles "no name column at all" case
    }, 640);
  });
}

// ── Step 4 — validation + duplicate preview ──────────────────────────────
// Resolves to one of:
//   { action: 'import', patients: [...], rowStates: [...] } — user clicked
//     Import. patients is the array the RPC will receive (SC7); rowStates
//     carries full per-row context for error-report generation later.
//   'back' — user clicked ← Back; caller re-opens the mapping step.
//   null — Cancel / X / backdrop.
function _stepValidationPreview(parseResult, mapping) {
  return new Promise(resolve => {
    const { openModal, el, mkBtn, DB } = H();
    if (!openModal) { resolve(null); return; }

    const parsedRows = (parseResult && parseResult.rows) || [];
    const headers = (parseResult && parseResult.headers) || [];

    const sps = (DB && DB.specialistPatients) || [];
    const children = (DB && DB.children) || [];

    // One-time pipeline run — produces per-row state. UI mutations only
    // touch each row's .decision (Skip / Create-anyway toggle for dupes).
    const rowStates = buildPreview(parsedRows, mapping, sps, children);

    // Tracked so we can lock them during the in-flight RPC and prevent
    // toggle-after-send drift between the visible state and what was
    // actually submitted.
    const dupeSelects = [];

    openModal(T('csv_review_title'), (mb, close) => {
      mb.classList.add('csv-modal-body');

      const summary = el('div', { class: 'csv-summary' });
      mb.appendChild(summary);

      const tableWrap = el('div', { class: 'csv-rows-wrap' });
      const table = el('table', { class: 'csv-rows-table' });

      const thead = el('thead');
      const headRow = el('tr');
      ['#', T('csv_col_status'), T('csv_col_name'), T('csv_col_dob'), T('csv_col_pronouns'), T('csv_col_parent_email'), T('csv_col_action')].forEach(h => {
        headRow.appendChild(el('th', {}, [h]));
      });
      thead.appendChild(headRow);
      table.appendChild(thead);

      const tbody = el('tbody');
      rowStates.forEach((r, i) => {
        const tr = el('tr', { class: 'csv-row csv-row-' + r.status });

        tr.appendChild(el('td', { class: 'csv-rownum' }, [String(i + 1)]));

        // Status cell — badge + detail lines for warnings / reason / dupes
        const stCell = el('td');
        const badgeLabel = r.status === 'ok' ? T('csv_badge_ok')
                         : r.status === 'flagged' ? T('csv_badge_flagged')
                         : r.status === 'duplicate' ? T('csv_badge_duplicate')
                         : T('csv_badge_invalid');
        stCell.appendChild(el('span', { class: 'csv-badge csv-badge-' + r.status }, [badgeLabel]));
        if (r.reason) {
          stCell.appendChild(el('div', { class: 'csv-row-detail csv-row-detail-err' }, [r.reason]));
        }
        if (r.warnings && r.warnings.length) {
          stCell.appendChild(el('div', { class: 'csv-row-detail' }, [r.warnings.join('; ')]));
        }
        if (r.dupes && r.dupes.length) {
          const label = r.dupes.map(d => T('csv_matches_existing', { source: d.source })).join(' · ');
          stCell.appendChild(el('div', { class: 'csv-row-detail' }, [label]));
        }
        tr.appendChild(stCell);

        tr.appendChild(el('td', {}, [r.patient.name || '—']));
        tr.appendChild(el('td', {}, [r.patient.dob || '—']));
        tr.appendChild(el('td', {}, [r.patient.pronouns || '—']));
        tr.appendChild(el('td', {}, [r.patient.parent_email_hint || '—']));

        // Action cell — Skip/Create-anyway select for dupes, otherwise
        // a static label.
        const actCell = el('td');
        if (r.status === 'invalid') {
          actCell.appendChild(el('span', { class: 'csv-action-excluded' }, [T('csv_excluded')]));
        } else if (r.status === 'duplicate') {
          const sel = el('select', { class: 'csv-action-select' });
          const optSkip = el('option', { value: 'skip' }, [T('csv_skip')]);
          const optCreate = el('option', { value: 'create-anyway' }, [T('csv_create_anyway')]);
          if (r.decision === 'create-anyway') optCreate.selected = true;
          else optSkip.selected = true;
          sel.appendChild(optSkip);
          sel.appendChild(optCreate);
          sel.onchange = () => { r.decision = sel.value; _refreshSummary(); };
          dupeSelects.push(sel);
          actCell.appendChild(sel);
        } else {
          actCell.appendChild(el('span', { class: 'csv-action-create' }, [T('csv_create')]));
        }
        tr.appendChild(actCell);

        tbody.appendChild(tr);
      });
      table.appendChild(tbody);
      tableWrap.appendChild(table);
      mb.appendChild(tableWrap);

      const btnRow = el('div', { class: 'csv-btn-row' });
      const backBtn = mkBtn(T('btn_back'), 'btn-md btn-ghost', () => { close(); resolve('back'); });
      backBtn.style.marginInlineEnd = 'auto';
      const cancelBtn = mkBtn(T('btn_cancel'), 'btn-md btn-ghost', () => { close(); resolve(null); });
      const importBtn = mkBtn(T('csv_import'), 'btn-md btn-primary', async () => {
        const c = _counts();
        if (c.toImport === 0) return; // button is disabled but be defensive

        // Snapshot the payload at click time — toggles after this point
        // (which we also lock below) wouldn't be reflected anyway. Keep
        // includedStates parallel to patients so we can map RPC error
        // indices back to original CSV rows for the SC8 error report.
        const includedStates = rowStates.filter(r => r.status !== 'invalid' && !(r.status === 'duplicate' && r.decision === 'skip'));
        const patients = includedStates.map(r => _toRpcRow(r.patient));

        // Loading state — disable all interactive controls so the user
        // can't double-submit or mutate the row set mid-RPC.
        const origLabel = importBtn.textContent;
        importBtn.disabled = true;
        importBtn.textContent = T('csv_importing');
        backBtn.disabled = true;
        cancelBtn.disabled = true;
        dupeSelects.forEach(s => { s.disabled = true; });

        const HUD = H();
        const S = HUD.S; // live ref via the bridge's getter
        // Realtime burst mitigation per spec §4d: the specialist_patients
        // listener fires _loadSpecialistPatients() on every INSERT event
        // and is NOT echo-suppressed. N inserts would fire N refetches.
        // Increment _suppressRefreshCount so listener calls during the
        // RPC are gated; the finally block does ONE authoritative refetch
        // when the counter hits 0.
        S._suppressRefreshCount = (S._suppressRefreshCount || 0) + 1;

        let success = false;
        let data = null;
        try {
          const res = await HUD._supa.rpc('bulk_create_specialist_patients', { p_patients: patients });
          if (res.error) throw res.error;
          if (!res.data || res.data.ok !== true) {
            throw new Error('RPC did not return ok');
          }
          success = true;
          data = res.data;
        } catch (e) {
          console.error('❌ csv bulk import:', e);
          const raw = (e && (e.message || e.hint || e.details)) || '';
          let friendly = T('csv_import_failed');
          if (raw.indexOf('not authenticated') >= 0) friendly = T('csv_signin_again');
          else if (raw.indexOf('batch too large') >= 0) friendly = T('csv_too_many');
          HUD.toast?.(friendly, 'error', 5000);
          // Re-enable controls so the user can retry / Cancel / Back.
          importBtn.disabled = false;
          importBtn.textContent = origLabel;
          backBtn.disabled = false;
          cancelBtn.disabled = false;
          dupeSelects.forEach(s => { s.disabled = false; });
        } finally {
          S._suppressRefreshCount = Math.max(0, (S._suppressRefreshCount || 0) - 1);
          if (S._suppressRefreshCount === 0) {
            try {
              if (typeof HUD._loadSpecialistPatients === 'function') {
                await HUD._loadSpecialistPatients();
              }
              HUD.re?.();
            } catch (refetchErr) {
              console.error('❌ csv post-import refetch:', refetchErr);
            }
          }
        }

        if (success) {
          const inserted = data.inserted || 0;
          const errors = data.errors || [];
          const total = patients.length;
          const failed = errors.length;

          if (failed === 0) {
            // Full success — SC7 behavior unchanged: toast + close.
            HUD.toast?.(T(inserted === 1 ? 'csv_imported_n' : 'csv_imported_n_plural', { count: inserted }), 'success', 4000);
            close();
            resolve({ action: 'imported', inserted, total, errors, rowStates });
          } else {
            // Partial success — replace the modal body with a result
            // panel offering an error-report download. NO toast (the
            // panel IS the feedback; toast would be redundant + would
            // close-then-stay-open visually). Done resolves the step.
            const panel = el('div', { class: 'csv-result-panel' });
            panel.appendChild(el('div', { class: 'csv-result-icon' }, ['⚠']));
            panel.appendChild(el('div', { class: 'csv-result-title' }, [T('csv_partial_title')]));
            const _countMsg = T(total === 1 ? 'csv_partial_count' : 'csv_partial_count_plural', { inserted, total });
            const _failMsg = T(failed === 1 ? 'csv_partial_failed' : 'csv_partial_failed_plural', { count: failed });
            panel.appendChild(el('div', { class: 'csv-result-summary' }, [_countMsg + ' ' + _failMsg]));
            panel.appendChild(el('div', { class: 'csv-result-hint' }, [T('csv_partial_hint')]));

            const resultBtnRow = el('div', { class: 'csv-btn-row' });
            const dlBtn = mkBtn('⬇ ' + T('csv_download_errors'), 'btn-md btn-secondary', () => {
              const csv = _buildErrorReportCsv(errors, includedStates, parsedRows, headers);
              _downloadCsv(csv, 'huddledin-import-errors.csv');
            });
            dlBtn.style.marginInlineEnd = 'auto';
            const doneBtn = mkBtn(T('btn_done'), 'btn-md btn-primary', () => {
              close();
              resolve({ action: 'imported', inserted, total, errors, rowStates });
            });
            resultBtnRow.appendChild(dlBtn);
            resultBtnRow.appendChild(doneBtn);

            mb.replaceChildren(panel, resultBtnRow);
          }
        }
        // If !success, the modal stays open with controls re-enabled.
        // resolve() is NOT called — the step's promise stays pending
        // until the user picks Cancel / Back / X, matching the cancel-
        // by-pending-promise semantic at the top of this file.
      });
      btnRow.appendChild(backBtn);
      btnRow.appendChild(cancelBtn);
      btnRow.appendChild(importBtn);
      mb.appendChild(btnRow);

      function _counts() {
        let toImport = 0, duplicates = 0, invalid = 0;
        for (const r of rowStates) {
          if (r.status === 'invalid') { invalid++; continue; }
          if (r.status === 'duplicate') {
            duplicates++;
            if (r.decision === 'create-anyway') toImport++;
            continue;
          }
          toImport++; // ok or flagged
        }
        return { toImport, duplicates, invalid };
      }

      function _refreshSummary() {
        const c = _counts();
        summary.textContent = T('csv_summary', { toImport: c.toImport, duplicates: c.duplicates, invalid: c.invalid });
        importBtn.textContent = c.toImport > 0
          ? T(c.toImport === 1 ? 'csv_import_n' : 'csv_import_n_plural', { count: c.toImport })
          : T('csv_import');
        importBtn.disabled = c.toImport === 0;
      }

      _refreshSummary();
    }, 900); // wider than other steps — 7-column preview table needs room
  });
}
